import { useApiGet } from 'hooks/useApi';
import Pagination from 'models/Pagination';
import Device from 'models/Device';
import { Playlist } from 'models/Playlist';
import { Campaign } from 'models/MediaFileCampaign';
import MediaFile from 'models/MediaFile';
import DeviceGroup from 'models/DeviceGroup';

export interface DashboardStats {
  devices: {
    total: number;
    active: number;
    inactive: number;
    online: number;
  };
  playlists: {
    total: number;
    active: number;
  };
  campaigns: {
    total: number;
    active: number;
  };
  medias: {
    total: number;
  };
  deviceGroups: {
    total: number;
  };
}

const useControllerDashboard = () => {
  // Fetch devices
  const { data: devicesData, isLoading: isLoadingDevices } = useApiGet<
    Pagination<Device>
  >('/device', {
    params: { pageSize: 1000, page: 1 }
  });

  // Fetch playlists
  const { data: playlistsData, isLoading: isLoadingPlaylists } = useApiGet<
    Pagination<Playlist>
  >('/media-manager/playlists', {
    params: { pageSize: 1000, page: 1 }
  });

  // Fetch campaigns
  const { data: campaignsData, isLoading: isLoadingCampaigns } = useApiGet<
    Pagination<Campaign>
  >('/media-manager/campaigns', {
    params: { pageSize: 1000, page: 1 }
  });

  // Fetch medias
  const { data: mediasData, isLoading: isLoadingMedias } = useApiGet<
    Pagination<MediaFile>
  >('/media-manager/medias', {
    params: { pageSize: 1, page: 1 }
  });

  // Fetch device groups
  const {
    data: deviceGroupsData,
    isLoading: isLoadingDeviceGroups
  } = useApiGet<DeviceGroup[]>('/device/group');

  const devices = devicesData?.items || [];
  const playlists = playlistsData?.items || [];
  const campaigns = campaignsData?.items || [];

  const stats: DashboardStats = {
    devices: {
      total: devicesData?.total || 0,
      active: devices.filter((d) => d.active).length,
      inactive: devices.filter((d) => !d.active).length,
      online: devices.filter((d) => d.player?.active).length
    },
    playlists: {
      total: playlistsData?.total || 0,
      active: playlists.filter((p) => p.active).length
    },
    campaigns: {
      total: campaignsData?.total || 0,
      active: campaigns.filter((c) => c.active).length
    },
    medias: {
      total: mediasData?.total || 0
    },
    deviceGroups: {
      total: deviceGroupsData?.length || 0
    }
  };

  const isLoading =
    isLoadingDevices ||
    isLoadingPlaylists ||
    isLoadingCampaigns ||
    isLoadingMedias ||
    isLoadingDeviceGroups;

  // Recent items for quick access
  const recentPlaylists = playlists.slice(0, 5);
  const recentCampaigns = campaigns.slice(0, 5);
  const recentDevices = devices.slice(0, 5);

  return {
    stats,
    isLoading,
    recentPlaylists,
    recentCampaigns,
    recentDevices,
    devices,
    deviceGroups: deviceGroupsData || []
  };
};

export default useControllerDashboard;
